<template>
  <div class="orders-page">
    <AppNavbar />

    <main class="container ds-content">
      <button type="button" class="ds-back-btn" @click="goBack">
        <i class="ri-arrow-left-line"></i> Back to My Orders
      </button>

      <div class="page-head">
        <div class="page-head-left">
          <h1 class="page-title">Delivery Status</h1>
          <p class="page-sub">
            Order #{{ orderId }} · {{ totalUnits }} units in this order
          </p>
        </div>
      </div>

      <!-- MISSING ORDER (e.g. page opened directly, not via My Orders) -->
      <div v-if="!orderFound" class="ds-empty">
        <div class="ds-empty-icon"><i class="ri-file-warning-line"></i></div>
        <p class="ds-empty-title">We couldn't find this order's details</p>
        <p class="ds-empty-sub">
          Open Delivery Status from an order on My Orders.
        </p>
        <button type="button" class="ds-empty-btn" @click="goBack">
          Back to My Orders
        </button>
      </div>

      <template v-else>
        <p v-if="isLoading" class="status-text">Loading delivery details...</p>
        <p v-else-if="errorMessage" class="status-text error">
          {{ errorMessage }}
        </p>

        <template v-else>
          <!-- OVERALL PROGRESS -->
          <!-- <section class="ds-summary">
            <div class="ds-summary-top">
              <span class="ds-fulfilment" :class="fulfilmentClass">
                {{ fulfilmentLabel }}
              </span>
              <span class="ds-count">
                {{ dispatchedUnits }} of {{ totalUnits }} units shipped
              </span>
            </div>
            <div class="ds-bar">
              <span
                class="ds-bar-fill"
                :style="{ width: progressPercent + '%' }"
              ></span>
            </div>
          </section> -->

          <!-- TABS -->
          <div class="ds-tabs" role="tablist">
            <button
              type="button"
              role="tab"
              class="ds-tab"
              :class="{ active: activeTab === 'shipped' }"
              :aria-selected="activeTab === 'shipped'"
              @click="activeTab = 'shipped'"
            >
              Shipped
              <span class="ds-tab-badge">{{ shipments.length }}</span>
            </button>
            <button
              type="button"
              role="tab"
              class="ds-tab"
              :class="{ active: activeTab === 'pending' }"
              :aria-selected="activeTab === 'pending'"
              @click="activeTab = 'pending'"
            >
              Pending Shipment
              <span class="ds-tab-badge">{{ pendingItems.length }}</span>
            </button>
          </div>

          <!-- SHIPPED TAB -->
          <section v-if="activeTab === 'shipped'" class="ds-panel">
            <div v-if="!shipments.length" class="ds-empty">
              <div class="ds-empty-icon"><i class="ri-truck-line"></i></div>
              <p class="ds-empty-title">Nothing shipped yet</p>
              <p class="ds-empty-sub">
                Everything in this order is still waiting to be packed.
              </p>
            </div>

            <div v-else class="ds-shipments">
              <article
                v-for="(shipment, index) in shipments"
                :key="shipment.key"
                class="ds-shipment"
              >
                <div class="ds-shipment-head">
                  <div class="ds-shipment-id">
                    <span class="ds-shipment-index">
                      Shipment {{ index + 1 }} of {{ shipments.length }}
                    </span>
                    <span class="ds-carrier">
                      <i class="ri-truck-line"></i>
                      {{ shipment.carrier }}
                      <span v-if="shipment.shipmentId" class="ds-awb">
                        {{ shipment.shipmentId }}
                      </span>
                    </span>
                  </div>
                  <span class="ds-pill" :class="shipment.statusClass">
                    {{ shipment.statusText }}
                  </span>
                </div>

                <ul class="ds-item-list">
                  <li
                    v-for="(line, lineIndex) in shipment.items"
                    :key="line.key + '-' + lineIndex"
                    class="ds-item"
                  >
                    <span class="ds-item-name">{{ line.itemName }}</span>
                    <span class="ds-item-qty"> Qty: {{ line.quantity }} </span>
                  </li>
                </ul>

                <dl class="ds-meta">
                  <div v-if="shipment.invoiceNumber" class="ds-meta-row">
                    <dt>Invoice</dt>
                    <dd>
                      {{ shipment.invoiceNumber }}
                      <span v-if="shipment.invoiceDate" class="ds-muted">
                        · {{ formatDate(shipment.invoiceDate) }}
                      </span>
                    </dd>
                  </div>
                  <div v-if="shipment.contactPerson" class="ds-meta-row">
                    <dt>Contact</dt>
                    <dd>
                      {{ shipment.contactPerson }}
                      <a
                        v-if="shipment.contactPhoneNumber"
                        :href="'tel:' + shipment.contactPhoneNumber"
                        class="ds-phone"
                      >
                        {{ shipment.contactPhoneNumber }}
                      </a>
                    </dd>
                  </div>
                </dl>
              </article>
            </div>
          </section>

          <!-- PENDING TAB -->
          <section v-else class="ds-panel">
            <div v-if="!pendingItems.length" class="ds-empty">
              <div class="ds-empty-icon">
                <i class="ri-checkbox-circle-line"></i>
              </div>
              <p class="ds-empty-title">Nothing pending</p>
              <p class="ds-empty-sub">
                Every item in this order has been shipped.
              </p>
            </div>

            <ul v-else class="ds-item-list ds-item-list--pending">
              <li
                v-for="line in pendingItems"
                :key="'pending-' + line.key"
                class="ds-item"
              >
                <span class="ds-item-name">{{ line.itemName }}</span>
                <span class="ds-item-qty ds-item-qty--pending">
                  Qty pending: {{ line.remaining }}
                </span>
              </li>
            </ul>
          </section>
        </template>
      </template>
    </main>

    <Footer />
  </div>
</template>

<script>
import { computed, onMounted, ref } from "vue";
import * as ecomSdk from "rdep-ecom-sdk";
import { useRoute, useRouter } from "vue-router";
import AppNavbar from "../AppNavbar.vue";
import Footer from "../FooterView.vue";

const getOrderDeliveries = ecomSdk.getOrderDeliveries;

export default {
  name: "DeliveryStatus",
  components: { AppNavbar, Footer },
  setup() {
    const route = useRoute();
    const router = useRouter();

    const order = ref(null);
    const orderFound = ref(false);
    const isLoading = ref(false);
    const errorMessage = ref("");
    const deliveries = ref([]);
    const activeTab = ref("shipped");

    const orderId = computed(() => String(route.params?.orderId || "").trim());

    function extractDeliveries(response) {
      if (Array.isArray(response)) return response;

      const data = response?.data || response || {};
      if (Array.isArray(data)) return data;
      const nested = data?.data || {};
      const candidates = [
        data.deliveries,
        nested.deliveries,
        data.deliveryData,
        nested.deliveryData,
        data.orderDeliveries,
        nested.orderDeliveries,
        data.content,
        nested.content,
      ];

      return candidates.find(Array.isArray) || [];
    }

    async function loadOrder() {
      const id = orderId.value;
      if (!id) {
        orderFound.value = false;
        return;
      }

      // OrderStatus.vue stashes the order the button was clicked on before
      // navigating here, since the order list already has everything this
      // page needs. Replace this with a real order-detail fetch if this
      // page should also work from a bookmarked / shared link.
      try {
        const raw = window.sessionStorage.getItem(`deliveryStatus.order.${id}`);
        if (raw) {
          order.value = JSON.parse(raw);
          orderFound.value = true;
        }
      } catch (error) {
        orderFound.value = false;
      }

      if (!orderFound.value) return;

      if (typeof getOrderDeliveries !== "function") {
        errorMessage.value = "Order delivery API is not available in the SDK.";
        return;
      }

      isLoading.value = true;
      errorMessage.value = "";
      try {
        deliveries.value = extractDeliveries(await getOrderDeliveries(id));
      } catch (error) {
        deliveries.value = [];
        errorMessage.value =
          error?.response?.data?.statusMessage ||
          error?.message ||
          "Failed to load delivery details.";
      } finally {
        isLoading.value = false;
      }
    }

    onMounted(loadOrder);

    function goBack() {
      router.push({ name: "order-status" });
    }

    const itemKey = (item) => {
      const code = String(item?.itemCode || "").trim();
      if (code) return `code:${code.toLowerCase()}`;
      return `name:${String(item?.itemName || "")
        .trim()
        .toLowerCase()}`;
    };

    const quantityOf = (item) => {
      const value = Number(
        item?.itemQuantity ?? item?.itemsCount ?? item?.quantity ?? 0,
      );
      return Number.isFinite(value) && value > 0 ? value : 1;
    };

    const orderedItems = computed(() => {
      const list = Array.isArray(order.value?.orderItems)
        ? order.value.orderItems
        : [];
      const merged = new Map();

      for (const item of list) {
        const key = itemKey(item);
        const existing = merged.get(key);
        if (existing) {
          existing.orderedQuantity += quantityOf(item);
          continue;
        }
        merged.set(key, {
          key,
          itemName: String(item?.itemName || "").trim() || "Product",
          itemCode: String(item?.itemCode || "").trim(),
          orderedQuantity: quantityOf(item),
        });
      }

      return Array.from(merged.values());
    });

    const orderedByKey = computed(() => {
      const map = new Map();
      for (const item of orderedItems.value) map.set(item.key, item);
      return map;
    });

    const rawDeliveries = computed(() => {
      return orderFound.value ? deliveries.value : [];
    });

    const normalizeStatus = (status) => {
      const raw = String(status || "").trim();
      const token = raw
        .toUpperCase()
        .replace(/[^A-Z0-9]+/g, "_")
        .replace(/^_+|_+$/g, "");

      if (token === "ITEMS_RESERVED" || token === "ITEM_RESERVED") {
        return { text: "Ready to ship", cls: "ds-pill--ready" };
      }
      if (
        ["DISPATCHED", "SHIPPED", "IN_TRANSIT", "OUT_FOR_DELIVERY"].includes(
          token,
        )
      ) {
        return { text: raw, cls: "ds-pill--transit" };
      }
      if (["DELIVERED", "COMPLETED", "COMPLETE"].includes(token)) {
        return { text: raw, cls: "ds-pill--delivered" };
      }
      if (["CANCELLED", "CANCELED", "RETURNED", "FAILED"].includes(token)) {
        return { text: raw, cls: "ds-pill--halted" };
      }
      return { text: raw || "Processing", cls: "ds-pill--ready" };
    };

    const shipments = computed(() =>
      rawDeliveries.value.map((delivery, index) => {
        const status = normalizeStatus(
          delivery?.status || delivery?.deliveryStatus,
        );
        const lines =
          delivery?.deliveryItems ||
          delivery?.items ||
          delivery?.orderItems ||
          [];

        return {
          key: String(
            delivery?.id || delivery?.shipmentId || `shipment-${index}`,
          ),
          carrier:
            String(
              delivery?.carrier ||
                delivery?.deliveryPartner ||
                delivery?.courierName ||
                "",
            ).trim() || "Carrier to be assigned",
          shipmentId: String(
            delivery?.shipmentId ||
              delivery?.trackingNumber ||
              delivery?.awb ||
              "",
          ).trim(),
          invoiceNumber: String(
            delivery?.invoiceNumber || delivery?.invoiceNo || "",
          ).trim(),
          invoiceDate: String(delivery?.invoiceDate || "").trim(),
          contactPerson: String(delivery?.contactPerson || "").trim(),
          contactPhoneNumber: String(delivery?.contactPhoneNumber || "").trim(),
          statusText: status.text,
          statusClass: status.cls,
          items: lines.map((line) => {
            const key = itemKey(line);
            const ordered = orderedByKey.value.get(key);
            return {
              key,
              itemName:
                String(line?.itemName || "").trim() ||
                ordered?.itemName ||
                "Product",
              quantity: quantityOf(line),
            };
          }),
        };
      }),
    );

    const dispatchedByKey = computed(() => {
      const map = new Map();
      for (const shipment of shipments.value) {
        for (const line of shipment.items) {
          map.set(line.key, (map.get(line.key) || 0) + line.quantity);
        }
      }
      return map;
    });

    const totalUnits = computed(() =>
      orderedItems.value.reduce((sum, item) => sum + item.orderedQuantity, 0),
    );

    const dispatchedUnits = computed(() =>
      orderedItems.value.reduce(
        (sum, item) =>
          sum +
          Math.min(
            dispatchedByKey.value.get(item.key) || 0,
            item.orderedQuantity,
          ),
        0,
      ),
    );

    const pendingItems = computed(() =>
      orderedItems.value
        .map((item) => ({
          ...item,
          remaining:
            item.orderedQuantity - (dispatchedByKey.value.get(item.key) || 0),
        }))
        .filter((item) => item.remaining > 0),
    );

    const progressPercent = computed(() =>
      totalUnits.value
        ? Math.round((dispatchedUnits.value / totalUnits.value) * 100)
        : 0,
    );

    const fulfilmentLabel = computed(() => {
      if (!dispatchedUnits.value) return "Awaiting dispatch";
      if (dispatchedUnits.value < totalUnits.value) return "Partly dispatched";
      return "Fully dispatched";
    });

    const fulfilmentClass = computed(() => {
      if (!dispatchedUnits.value) return "ds-fulfilment--waiting";
      if (dispatchedUnits.value < totalUnits.value)
        return "ds-fulfilment--partial";
      return "ds-fulfilment--full";
    });

    const formatDate = (value) => {
      if (!value) return "";
      const parsed = new Date(String(value).replace(" ", "T"));
      if (isNaN(parsed.getTime())) return String(value);
      return parsed.toLocaleDateString("en-IN", {
        day: "numeric",
        month: "short",
        year: "numeric",
      });
    };

    return {
      orderId,
      orderFound,
      isLoading,
      errorMessage,
      activeTab,
      shipments,
      totalUnits,
      dispatchedUnits,
      pendingItems,
      progressPercent,
      fulfilmentLabel,
      fulfilmentClass,
      formatDate,
      goBack,
    };
  },
};
</script>

<style scoped>
@import url("https://cdn.jsdelivr.net/npm/remixicon@4.2.0/fonts/remixicon.css");

.orders-page {
  min-height: 100vh;
  background: #f0f4f9;
}
.container {
  width: 92%;
  max-width: 760px;
  margin: 0 auto;
}
.ds-content {
  padding: 28px 0 72px;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

/* BACK BUTTON */
.ds-back-btn {
  align-self: flex-start;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 1.5px solid #d1dbe8;
  border-radius: 999px;
  background: #fff;
  color: #344760;
  font-size: 13px;
  font-weight: 700;
  padding: 7px 16px;
  cursor: pointer;
  font-family: inherit;
  transition: all 0.18s;
}
.ds-back-btn:hover {
  border-color: #003da5;
  color: #003da5;
}

/* PAGE HEAD */
.page-head-left {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.page-title {
  font-size: 24px;
  font-weight: 800;
  color: #0a2240;
  margin: 0;
}
.page-sub {
  font-size: 13px;
  color: #5a6b82;
  margin: 0;
}

/* SUMMARY */
.ds-summary {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.ds-summary-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  flex-wrap: wrap;
}
.ds-fulfilment {
  font-size: 13px;
  font-weight: 800;
}
.ds-fulfilment--waiting {
  color: #92400e;
}
.ds-fulfilment--partial {
  color: #003da5;
}
.ds-fulfilment--full {
  color: #3b6d11;
}
.ds-count {
  font-size: 12px;
  color: #5a6b82;
  font-weight: 600;
}
.ds-bar {
  height: 6px;
  border-radius: 999px;
  background: #e6edf7;
  overflow: hidden;
}
.ds-bar-fill {
  display: block;
  height: 100%;
  border-radius: 999px;
  background: linear-gradient(to right, #003da5, #0051cc);
  transition: width 0.3s ease;
}

/* TABS */
.ds-tabs {
  display: flex;
  gap: 6px;
  border-bottom: 1.5px solid #dbe4f0;
}
.ds-tab {
  border: none;
  background: none;
  cursor: pointer;
  font-family: inherit;
  padding: 10px 4px 12px;
  margin-right: 18px;
  font-size: 13px;
  font-weight: 700;
  color: #94a3b8;
  display: inline-flex;
  align-items: center;
  gap: 7px;
  border-bottom: 2.5px solid transparent;
  transform: translateY(1.5px);
  transition: color 0.15s, border-color 0.15s;
}
.ds-tab:hover {
  color: #344760;
}
.ds-tab.active {
  color: #003da5;
  border-bottom-color: #003da5;
}
.ds-tab-badge {
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  border-radius: 999px;
  background: #eef2f8;
  color: #5a6b82;
  font-size: 11px;
  font-weight: 800;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.ds-tab.active .ds-tab-badge {
  background: #dbeafe;
  color: #003da5;
}

/* PANEL */
.ds-panel {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

/* SHIPMENTS */
.ds-shipments {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.ds-shipment {
  background: #fff;
  border: 1px solid rgba(0, 61, 165, 0.08);
  border-radius: 16px;
  padding: 16px 18px;
  box-shadow: 0 2px 12px rgba(0, 61, 165, 0.04);
}
.ds-shipment-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
  flex-wrap: wrap;
}
.ds-shipment-id {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}
.ds-shipment-index {
  font-size: 13px;
  font-weight: 800;
  color: #0a2240;
}
.ds-carrier {
  font-size: 12px;
  color: #5a6b82;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
}
.ds-awb {
  font-weight: 700;
  color: #344760;
}
.ds-pill {
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  padding: 4px 10px;
  border-radius: 999px;
  white-space: nowrap;
}
.ds-pill--ready {
  background: #fef3c7;
  color: #92400e;
}
.ds-pill--transit {
  background: #dbeafe;
  color: #1d4ed8;
}
.ds-pill--delivered {
  background: #eaf3de;
  color: #3b6d11;
}
.ds-pill--halted {
  background: #fee2e2;
  color: #b91c1c;
}

/* ITEM LINES */
.ds-item-list {
  list-style: none;
  margin: 12px 0 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.ds-item-list--pending {
  margin-top: 0;
}
.ds-item {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  font-size: 13px;
  color: #0a2240;
  padding: 10px 12px;
  background: #f7faff;
  border-radius: 10px;
}
.ds-item-name {
  font-weight: 700;
  min-width: 0;
}
.ds-item-qty {
  font-size: 12px;
  font-weight: 700;
  color: #3b6d11;
  white-space: nowrap;
}
.ds-item-qty--pending {
  color: #92400e;
}

/* META */
.ds-meta {
  margin: 12px 0 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.ds-meta-row {
  display: flex;
  gap: 8px;
  font-size: 12px;
}
.ds-meta-row dt {
  color: #94a3b8;
  font-weight: 600;
  min-width: 62px;
}
.ds-meta-row dd {
  margin: 0;
  color: #344760;
  font-weight: 600;
}
.ds-muted {
  color: #94a3b8;
  font-weight: 500;
}
.ds-phone {
  color: #003da5;
  text-decoration: none;
  margin-left: 6px;
}
.ds-phone:hover {
  text-decoration: underline;
}

/* EMPTY */
.ds-empty {
  text-align: center;
  padding: 48px 12px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}
.ds-empty-icon {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: rgba(0, 61, 165, 0.06);
  color: #003da5;
  font-size: 28px;
  display: grid;
  place-items: center;
}
.ds-empty-title {
  margin: 0;
  font-size: 15px;
  font-weight: 700;
  color: #0a2240;
}
.ds-empty-sub {
  margin: 0;
  font-size: 13px;
  color: #94a3b8;
  max-width: 340px;
}
.ds-empty-btn {
  margin-top: 8px;
  border: none;
  border-radius: 999px;
  background: #003da5;
  color: #fff;
  font-size: 13px;
  font-weight: 700;
  padding: 9px 18px;
  cursor: pointer;
  font-family: inherit;
}
.ds-empty-btn:hover {
  background: #003083;
}

.status-text {
  margin: 0;
  font-size: 14px;
  color: #5a6b82;
}
.status-text.error {
  color: #b91c1c;
  font-weight: 700;
}

@media (max-width: 600px) {
  .ds-shipment {
    padding: 14px;
  }
}
</style>
